import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const parsedCount = parseInt(this.refs.cartBubbleCount.dataset.count ?? '', 10);
    return Number.isFinite(parsedCount) ? parsedCount : 0;
  }

  set currentCartCount(value) {
    const normalizedValue = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
    this.refs.cartBubbleCount.dataset.count = String(normalizedValue);
    this.refs.cartBubbleCount.textContent = normalizedValue > 99 ? '99+' : String(normalizedValue);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the standard cart lines update event by reconciling once the request settles.
   * @param {Event} event - The cart lines update event.
   */
  onCartUpdate = (event) => {
    const linesEvent = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);

    linesEvent.promise
      ?.then((result) => {
        const cart = /** @type {{ totalQuantity?: number } | null} */ (/** @type {any} */ (result)?.cart);
        const detail = /** @type {any} */ (result)?.detail;

        // Prefer the standard cart summary's absolute total quantity as the source of truth.
        const totalQuantity = typeof cart?.totalQuantity === 'number' ? cart.totalQuantity : null;
        if (totalQuantity != null) {
          this.renderCartBubble(totalQuantity, false);
          return;
        }

        // Fallback for when the cart summary is unavailable (e.g. /cart.js failed): approximate from
        // the raw AJAX response, or optimistically add for product-form adds.
        const resource = /** @type {{ item_count?: number } | undefined} */ (detail?.resource);
        const resourceItemCount = typeof resource?.item_count === 'number' ? resource.item_count : undefined;
        const comingFromProductForm = detail?.source === 'product-form-component';

        if (detail?.didError && resourceItemCount == null && !comingFromProductForm) return;

        const itemCount = resourceItemCount ?? detail?.itemCount ?? 0;
        this.renderCartBubble(itemCount, comingFromProductForm && resourceItemCount == null);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] cart update promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.currentCartCount = nextCount;
    this.refs.cartBubble.toggleAttribute('data-maintain-ratio', nextCount <= 99);

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.dataset.count ?? this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
